/**
 * The design tokens — the single source of truth for every colour, size and duration in Tvitvi.
 *
 * WHY THIS IS TYPESCRIPT IN `@tvitvi/shared` AND NOT A CSS FILE (DESIGN.md §10).
 *
 * A native mobile app is committed, and React Native cannot read `tokens.css`. So the values live
 * here as plain typed objects — no CSS, no DOM, no colour library — and
 * `scripts/generate-css-tokens.ts` in apps/web emits the custom properties from them. A test
 * fails if the generated file is stale, so the two cannot disagree.
 *
 * GLASS 2 (DESIGN.md §16, 29 September 2026). Four axes instead of one: mode (`ColorMode`),
 * backdrop, glass level and accent. The first is `colorsByMode`, as before; the three new ones
 * are `backdrops`, `glassLevels` and `accents` below. The values are PROMPT.md's appendix A.
 * Token NAMES from before are kept wherever they still mean something, so every component kept
 * working the day the values changed.
 */

/** A token set that differs per colour mode. */
export interface ModeColors {
  /** The «Flat» ground. The shell paints the backdrop layer, not this. */
  readonly app: string;
  /** The content card, OPAQUE — for sticky headers and anything that must never show through. */
  readonly surface: string;
  /** The tonal fill made opaque over `surface`. Translucent use: `glassSurfaces.fillTonal`. */
  readonly surfaceSunken: string;
  /** Dialogs and menus, opaque. In dark mode it is lighter than `surface` (§4). */
  readonly surfaceRaised: string;
  readonly overlay: string;

  /**
   * The bar a skeleton draws while content loads. The DIRECTION flips between modes (§4): darker
   * than the card in light, lighter in dark, and with more separation in dark, because the same
   * delta reads weaker against a dark ground.
   */
  readonly skeleton: string;

  /** Radlinje — the hairline between table rows. Decorative, exempt from 3:1. */
  readonly borderSubtle: string;
  /** Skillelinje — between sections. Decorative, exempt from 3:1. */
  readonly borderDefault: string;
  /**
   * The boundary of a control that is drawn WITH an edge — checkbox, radio. WCAG 1.4.11 asks 3:1.
   * Glass 2 (G6): «kant betyr inndatafelt», so buttons no longer carry one at all.
   */
  readonly borderStrong: string;
  /**
   * THE FOCUS INDICATOR: the control's own edge, thickened to 2 px and darkened (Atle, 28 August:
   * «bare … litt mer bold og litt tykkere strek»). Drawn as an inset shadow, so it survives on a
   * filled field that has no border at rest. 3:1 against every ground it is drawn on.
   */
  readonly borderFocus: string;

  readonly textDefault: string;
  /** Dempet. `rgba(60,60,67,.76)` — .72 measured 4.36:1 on «Mye», .76 clears 4.5 everywhere. */
  readonly textMuted: string;
  /**
   * Section labels, helper text, metadata. It is TEXT and holds the 4.5:1 floor, so it carries
   * the muted value. Appendix A's «svak» is below that floor and is `textFaint`.
   */
  readonly textSubtle: string;
  /**
   * «Svak» from appendix A. Only for what need not be read: placeholder, the «—» of an empty
   * cell, a disabled label. Below 4.5:1 by design — never a sentence.
   */
  readonly textFaint: string;
  readonly textInverse: string;

  /* The accent quartet in this set is the DEFAULT accent (blue). The CSS follows `data-accent`. */
  readonly accent: string;
  readonly accentHover: string;
  /** Aksentfyll: selected menu row, an active filter chip. */
  readonly accentSubtle: string;
  /** Aksenttekst: tertiary buttons, the selected menu row, links. */
  readonly accentText: string;
  /**
   * The accent as a FILL UNDER SMALL TEXT: a count badge, today's date in a small circle. White
   * on Apple blue is 4.02:1 (light) and 3.65:1 (dark) — a number in a 16 px circle needs 4.5.
   * Everywhere else the fill is `accent`; the primary button waits for Atle (sak 226.6).
   */
  readonly accentStrong: string;
  /** Text ON the accent. A missing token here once rendered a badge at 1.3:1. */
  readonly onAccent: string;

  readonly success: string;
  readonly successSubtle: string;
  readonly warning: string;
  readonly warningSubtle: string;
  /** Favorittstjerna. Gull, ikke advarsel. */
  readonly favourite: string;
  readonly danger: string;
  readonly dangerSubtle: string;
  /** Teal since Glass 2 (G3), so info is never mistaken for a blue accent. */
  readonly info: string;
  readonly infoSubtle: string;

  /**
   * Categorical colours for things the CUSTOMER defines: departments, stages, tags. Assigned by
   * a stable hash of the entity id, never hardcoded per organisation (principle 9). Eight is
   * deliberate — beyond that they stop being distinguishable.
   */
  readonly categorical: readonly [string, string, string, string, string, string, string, string];
}

/** Status fills are the status colour at 13–17 % (appendix A). */
export const STATUS_FILL_ALPHA = 0.15;

/**
 * LIGHT MODE. Cool greys now (appendix A, interpretation 3) — Apple's `rgba(60,60,67,…)` family —
 * replacing the warm ramp of §3. The values are Atle's, 29 September.
 */
export const lightColors: ModeColors = {
  app: '#f2f2f7',
  surface: '#ffffff',
  surfaceSunken: '#efeff0',
  surfaceRaised: '#ffffff',
  overlay: 'rgb(20 20 30 / 0.32)',
  skeleton: '#e8e8ed',

  borderSubtle: 'rgba(60,60,67,0.08)',
  borderDefault: 'rgba(60,60,67,0.11)',
  borderStrong: '#7c7c82',
  borderFocus: '#3a3a3c',

  textDefault: '#1d1d1f',
  textMuted: 'rgba(60,60,67,0.76)',
  textSubtle: 'rgba(60,60,67,0.76)',
  textFaint: 'rgba(60,60,67,0.58)',
  textInverse: '#ffffff',

  accent: '#007aff',
  accentHover: '#0070eb',
  accentSubtle: 'rgba(0,122,255,0.12)',
  accentText: '#0062cc',
  accentStrong: '#0062cc',
  onAccent: '#ffffff',

  success: '#1f8a3b',
  successSubtle: 'rgba(31,138,59,0.15)',
  warning: '#b25000',
  warningSubtle: 'rgba(178,80,0,0.15)',
  favourite: '#eab308',
  danger: '#d70015',
  dangerSubtle: 'rgba(215,0,21,0.15)',
  info: '#0a7a86',
  infoSubtle: 'rgba(10,122,134,0.15)',

  categorical: [
    '#4a5fc4',
    '#1d7d68',
    '#9c5330',
    '#7146ad',
    '#a83370',
    '#2b7690',
    '#7d6c1e',
    '#5c5c63',
  ],
};

/**
 * DARK MODE — first class, not a filter (DESIGN.md §4). The ground is nearly black UNDER the
 * backdrop (appendix A), and surfaces still rise with lightness: raised > surface > app.
 */
export const darkColors: ModeColors = {
  app: '#111114',
  surface: '#1e1e26',
  surfaceSunken: '#33333c',
  surfaceRaised: '#2a2a33',
  overlay: 'rgb(0 0 0 / 0.6)',
  skeleton: '#3a3a45',

  borderSubtle: 'rgba(255,255,255,0.07)',
  borderDefault: 'rgba(255,255,255,0.09)',
  borderStrong: '#8e8e93',
  borderFocus: '#d1d1d6',

  textDefault: '#f5f5f7',
  textMuted: 'rgba(235,235,245,0.64)',
  textSubtle: 'rgba(235,235,245,0.64)',
  textFaint: 'rgba(235,235,245,0.5)',
  textInverse: '#111114',

  accent: '#0a84ff',
  accentHover: '#3395ff',
  accentSubtle: 'rgba(10,132,255,0.22)',
  accentText: '#64b0ff',
  accentStrong: '#0071e3',
  onAccent: '#ffffff',

  success: '#30d158',
  successSubtle: 'rgba(48,209,88,0.15)',
  warning: '#ffb340',
  warningSubtle: 'rgba(255,179,64,0.15)',
  favourite: '#facc15',
  danger: '#ff6961',
  dangerSubtle: 'rgba(255,105,97,0.15)',
  info: '#5ad1e0',
  infoSubtle: 'rgba(90,209,224,0.15)',

  categorical: [
    '#8b9bf0',
    '#4fc0a3',
    '#dd9268',
    '#ab8ce0',
    '#e277ae',
    '#5fb2d2',
    '#c3b158',
    '#98989d',
  ],
};

export const colorsByMode = { light: lightColors, dark: darkColors } as const;
export type ColorMode = keyof typeof colorsByMode;

/* ------------------------------------------------------------------------------------------ */
/* GLASS 2: the three new axes                                                                  */
/* ------------------------------------------------------------------------------------------ */

export type GlassLevel = 0 | 1 | 2 | 3;
export const GLASS_LEVELS: readonly GlassLevel[] = [0, 1, 2, 3];

export interface GlassLevelValues {
  /** `backdrop-filter` blur, px. 0 means no `backdrop-filter` at all. */
  readonly blur: number;
  /** `saturate()`, percent. */
  readonly saturate: number;
  /** Alpha of the content card (tables, forms). */
  readonly content: number;
  /** Alpha of the navigation layer (sidebar, topbar). */
  readonly nav: number;
  /** Alpha of a control lying directly on the backdrop. */
  readonly control: number;
  /**
   * Alpha of a menu or popover. NOT in appendix A — set by agent T: a menu opens over a table,
   * and at the navigation alpha (.30 on «Mye») the rows behind it would read through the options.
   */
  readonly popover: number;
}

/** Appendix A: Av · Lett · Standard · Mye. */
export const glassLevels: Readonly<Record<GlassLevel, GlassLevelValues>> = {
  0: { blur: 0, saturate: 100, content: 1, nav: 1, control: 1, popover: 1 },
  1: { blur: 20, saturate: 160, content: 0.95, nav: 0.75, control: 0.8, popover: 0.94 },
  2: { blur: 28, saturate: 180, content: 0.86, nav: 0.45, control: 0.62, popover: 0.88 },
  3: { blur: 36, saturate: 190, content: 0.72, nav: 0.3, control: 0.45, popover: 0.8 },
};

export const DEFAULT_GLASS: GlassLevel = 2;

/**
 * The glass surfaces per mode. A colour given as `contentRgb` is the `r g b` triplet the CSS
 * writes as `rgb(<triplet> / var(--alpha-content))`, so the glass level can set the alpha
 * without the mode knowing about it.
 */
export interface GlassSurfaces {
  readonly contentRgb: string;
  readonly contentBorder: string;
  /** The card shadow while glass is on. */
  readonly contentShadow: string;
  /** At glass 0 the card is opaque on a flat ground, so it needs a hairline ring instead. */
  readonly contentShadowSolid: string;
  readonly navRgb: string;
  readonly navBorder: string;
  readonly navShadow: string;
  readonly navShadowSolid: string;
  /** Tonal fill INSIDE a card. */
  readonly fillTonal: string;
  readonly fillTonalHover: string;
  /**
   * Tonal fill of a control lying ON the backdrop: white at `alpha-control` in light, and at
   * `alpha-control × onBackdropScale` in dark (appendix A: «alfa × .16»).
   */
  readonly fillOnBackdropRgb: string;
  readonly fillOnBackdropScale: number;
  readonly segmentActive: string;
  readonly segmentActiveShadow: string;
}

export const glassSurfaces: Readonly<Record<ColorMode, GlassSurfaces>> = {
  light: {
    contentRgb: '255 255 255',
    contentBorder: 'rgba(255,255,255,0.9)',
    contentShadow: '0 1px 2px rgba(30,40,90,0.05), 0 12px 32px -16px rgba(30,40,90,0.18)',
    contentShadowSolid: '0 1px 2px rgba(30,40,90,0.05), 0 0 0 1px rgba(60,60,67,0.07)',
    navRgb: '250 250 253',
    navBorder: 'rgba(255,255,255,0.7)',
    navShadow: '0 1px 2px rgba(30,40,90,0.05), 0 12px 32px -16px rgba(30,40,90,0.18)',
    navShadowSolid: '0 0 0 1px rgba(60,60,67,0.07)',
    fillTonal: 'rgba(118,118,128,0.12)',
    fillTonalHover: 'rgba(118,118,128,0.18)',
    fillOnBackdropRgb: '255 255 255',
    fillOnBackdropScale: 1,
    segmentActive: '#ffffff',
    segmentActiveShadow: '0 1px 2px rgba(0,0,0,0.08), 0 1px 6px -2px rgba(0,0,0,0.1)',
  },
  dark: {
    contentRgb: '30 30 38',
    contentBorder: 'rgba(255,255,255,0.08)',
    contentShadow: '0 20px 50px -24px rgba(0,0,0,0.8)',
    contentShadowSolid: '0 0 0 1px rgba(255,255,255,0.07)',
    navRgb: '30 30 40',
    navBorder: 'rgba(255,255,255,0.12)',
    navShadow: '0 20px 50px -24px rgba(0,0,0,0.8)',
    navShadowSolid: '0 0 0 1px rgba(255,255,255,0.07)',
    fillTonal: 'rgba(118,118,128,0.24)',
    fillTonalHover: 'rgba(118,118,128,0.32)',
    fillOnBackdropRgb: '255 255 255',
    fillOnBackdropScale: 0.16,
    segmentActive: 'rgba(255,255,255,0.22)',
    segmentActiveShadow: 'none',
  },
};

export type Accent = 'blue' | 'violet' | 'graphite';
export const ACCENTS: readonly Accent[] = ['blue', 'violet', 'graphite'];
export const DEFAULT_ACCENT: Accent = 'blue';

export interface AccentValues {
  readonly accent: string;
  readonly hover: string;
  /** Aksentfyll. */
  readonly fill: string;
  /** Aksenttekst. */
  readonly text: string;
  /** Tekst på aksent. */
  readonly onAccent: string;
  /** The accent under SMALL text on it (a count badge): `onAccent` on it holds 4.5:1. */
  readonly strong: string;
  /** The primary button's highlight: `mix(accent 82 %, white)` — the spec recomputes it. */
  readonly gradientTop: string;
  /** The primary button's glow: the accent at 60 %. */
  readonly glow: string;
  /** Row hover: the accent at 5 %. */
  readonly rowHover: string;
}

/** Appendix A, the accent table. Status colours never follow the accent. */
export const accents: Readonly<Record<Accent, Readonly<Record<ColorMode, AccentValues>>>> = {
  blue: {
    light: {
      accent: '#007aff',
      strong: '#0062cc',
      hover: '#0070eb',
      fill: 'rgba(0,122,255,0.12)',
      text: '#0062cc',
      onAccent: '#ffffff',
      gradientTop: '#2e92ff',
      glow: 'rgba(0,122,255,0.6)',
      rowHover: 'rgba(0,122,255,0.05)',
    },
    dark: {
      accent: '#0a84ff',
      strong: '#0071e3',
      hover: '#3395ff',
      fill: 'rgba(10,132,255,0.22)',
      text: '#64b0ff',
      onAccent: '#ffffff',
      gradientTop: '#369aff',
      glow: 'rgba(10,132,255,0.6)',
      rowHover: 'rgba(10,132,255,0.05)',
    },
  },
  violet: {
    light: {
      accent: '#5b2fc9',
      strong: '#5b2fc9',
      hover: '#4a24a8',
      fill: 'rgba(91,47,201,0.12)',
      text: '#4a24a8',
      onAccent: '#ffffff',
      gradientTop: '#7954d3',
      glow: 'rgba(91,47,201,0.6)',
      rowHover: 'rgba(91,47,201,0.05)',
    },
    dark: {
      accent: '#a98bff',
      strong: '#a98bff',
      hover: '#bda6ff',
      fill: 'rgba(169,139,255,0.2)',
      text: '#c3aeff',
      onAccent: '#14121c',
      gradientTop: '#b8a0ff',
      glow: 'rgba(169,139,255,0.6)',
      rowHover: 'rgba(169,139,255,0.05)',
    },
  },
  graphite: {
    light: {
      accent: '#2c2c2e',
      strong: '#2c2c2e',
      hover: '#1d1d1f',
      fill: 'rgba(60,60,67,0.1)',
      text: '#1d1d1f',
      onAccent: '#ffffff',
      gradientTop: '#525254',
      glow: 'rgba(44,44,46,0.6)',
      rowHover: 'rgba(44,44,46,0.05)',
    },
    dark: {
      accent: '#e5e5ea',
      strong: '#e5e5ea',
      hover: '#f2f2f7',
      fill: 'rgba(255,255,255,0.12)',
      text: '#f5f5f7',
      onAccent: '#111114',
      gradientTop: '#eaeaee',
      glow: 'rgba(229,229,234,0.6)',
      rowHover: 'rgba(229,229,234,0.05)',
    },
  },
};

export type Backdrop = 'aurora' | 'sunset' | 'fjord' | 'graphite' | 'flat';
export const BACKDROPS: readonly Backdrop[] = ['fjord', 'aurora', 'sunset', 'graphite', 'flat'];
export const DEFAULT_BACKDROP: Backdrop = 'fjord';

export interface BackdropScene {
  readonly base: string;
  /** Five, or none for «Flat». */
  readonly spots: readonly string[];
}

/** Appendix A: ground + five spots, light / dark. */
export const backdrops: Readonly<Record<Backdrop, Readonly<Record<ColorMode, BackdropScene>>>> = {
  aurora: {
    light: { base: '#eef1f8', spots: ['#9ec1ff', '#ffc2dc', '#b6f0de', '#d9c6ff', '#ffe3b0'] },
    dark: { base: '#06070c', spots: ['#2446b8', '#8a1f6a', '#0d6b69', '#4b2ab0', '#7a4a0f'] },
  },
  sunset: {
    light: { base: '#f8f1ee', spots: ['#ffc9a8', '#ffb3c7', '#ffe0a3', '#e9c2ff', '#ffd6cc'] },
    dark: { base: '#0b0708', spots: ['#8a3a1a', '#7a1f4a', '#6b4a0f', '#4a2a7a', '#5a2020'] },
  },
  fjord: {
    light: { base: '#eef3f4', spots: ['#a8d8e8', '#b8e6d4', '#c4d4f4', '#d6ecd8', '#b0cfe0'] },
    dark: { base: '#05090b', spots: ['#0d4a5a', '#0f5a46', '#1c3a78', '#1e4a3a', '#0d3050'] },
  },
  graphite: {
    light: { base: '#eeeff1', spots: ['#d9dbe0', '#e6e2dc', '#cfd3da', '#e2e4e8', '#d6d2cc'] },
    dark: { base: '#0b0b0d', spots: ['#2a2c33', '#33302b', '#25282e', '#2e3036', '#2b2925'] },
  },
  flat: {
    light: { base: '#f2f2f7', spots: [] },
    dark: { base: '#111114', spots: [] },
  },
};

/** How strongly the spots show: 60 % in light, 40 % in dark (appendix A). */
export const backdropCoverage: Readonly<Record<ColorMode, number>> = { light: 0.6, dark: 0.4 };

/**
 * Where the five spots sit and how large they are, as ellipse radii and centres in percent of the
 * viewport. Translated from the reference's five blurred discs (1536 × 1024 frame) into radial
 * gradients, because `filter: blur()` on a full-screen element is what §16.3 forbids.
 */
export const backdropSpotGeometry: readonly {
  readonly rx: number;
  readonly ry: number;
  readonly x: number;
  readonly y: number;
}[] = [
  { rx: 31, ry: 42, x: 13, y: 13 },
  { rx: 29, ry: 39, x: 87, y: 21 },
  { rx: 33, ry: 39, x: 53, y: 68 },
  { rx: 25, ry: 34, x: 78, y: 93 },
  { rx: 21, ry: 30, x: 10, y: 91 },
];

/* ------------------------------------------------------------------------------------------ */
/* Type, space, radius, layout, motion                                                          */
/* ------------------------------------------------------------------------------------------ */

/**
 * TYPOGRAPHY (G1). The system font — SF on Apple, Inter everywhere else (Windows has no SF), with
 * Inter self-hosted via @fontsource. JetBrains Mono stays for ids and time codes.
 */
export const typography = {
  fontSans:
    "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter Variable', system-ui, sans-serif",
  fontMono: "'JetBrains Mono Variable', ui-monospace, 'SF Mono', Consolas, monospace",
  /** Interpretation 5: a tail on l, serifs on I — Inter only; SF has neither feature. */
  fontFeatures: "'cv05', 'cv08'",

  /**
   * 13px is the BASE UI size, not 16. This is a dense operations tool, and the decision is made
   * once here rather than argued per component. Nothing a user must READ goes below 12.
   */
  size: {
    '2xs': '0.6875rem', // 11px — badges, kbd hints, never a sentence
    xs: '0.75rem', // 12px — table metadata, captions
    sm: '0.8125rem', // 13px — BASE UI SIZE
    md: '0.875rem', // 14px — body copy in forms
    lg: '1rem', // 16px — section titles
    xl: '1.25rem', // 20px — page titles before Glass 2
    '2xl': '1.5rem', // 24px — auth pages, and numbers that ARE the point
  },
  leading: { tight: '1.25', normal: '1.45' },
  tracking: { tight: '-0.01em' },

  /** Appendix A. */
  pageTitle: { size: '26px', weight: '700', tracking: '-0.022em' },
  sectionTitle: { size: '15px', weight: '600' },
} as const;

/** 4px base rhythm. Eight steps, and no component invents a ninth. */
export const spacing = {
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.5rem',
  6: '2rem',
  7: '3rem',
} as const;

/**
 * RADIUS (G4): card 22 · inner 14 · control 999 · segment 9 · menu row 10 · checkbox 5.
 *
 * The four old steps are kept by NAME and moved onto the new scale (xs = check, sm = segment,
 * md = inner, lg = card), so every `rounded-*` in the product lands on a Glass 2 value the day
 * this changes, before K1/K2 move each component to the semantic names.
 */
export const radius = {
  xs: '5px',
  sm: '9px',
  md: '14px',
  lg: '22px',
  full: '9999px',
  card: '22px',
  inner: '14px',
  control: '999px',
  segment: '9px',
  menuRow: '10px',
  check: '5px',
  /** The topbar — a bar 44 px tall reads as a slab at 22, as in the reference. */
  bar: '18px',
} as const;

/**
 * LAYOUT.
 *
 * `rowHeight` is 36px and `rowHeightCompact` 30px — Glass 2 leaves both alone (§1). 36 keeps a
 * running order scannable with two more rows per screen than 40, and still gives a full-width
 * target a cold finger can hit. `rowHeightRelaxed` 44px is for lists with pictures in them.
 */
export const layout = {
  sidebarWidth: '232px',
  topbarHeight: '44px',
  rowHeight: '36px',
  rowHeightCompact: '30px',
  rowHeightRelaxed: '44px',
  contentMax: '1440px',
  /** DESIGN.md §9: the floor for anything a finger must hit. */
  touchTarget: '44px',
  /** The air around the floating sidebar and topbar. */
  shellGap: '12px',

  /**
   * THE FIELD. `fieldHeight` is the box; `fieldLabelHeight` is the label that straddles its top
   * edge, so the footprint is 40 + 12 / 2 = 46px — against ~56px for a label above an input.
   */
  fieldHeight: '40px',
  fieldLabelHeight: '12px',
} as const;

/**
 * MOTION. DESIGN.md §6: only functional, 150–250ms, always respecting prefers-reduced-motion.
 * `fast` sits below that band on purpose — hover and focus, where anything perceptible is lag.
 */
export const motion = {
  durationFast: '120ms',
  durationBase: '180ms',
  durationSlow: '240ms',
  easeOut: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
} as const;

/**
 * FOCUS geometry. The indicator is the control's own edge, 2 px, drawn inside (globals.css).
 * `extent` is what anything stacked under a focusable control keeps clear of.
 */
export const focus = {
  offset: '2px',
  width: '2px',
  extent: '4px',
} as const;

/** Appendix A, the active tab: a 2.5 px accent underline. */
export const tabs = { underlineWidth: '2.5px' } as const;

/** Menus and dialogs float; a hairline ring keeps them edged on glass. */
export const elevation = {
  light: {
    popover:
      '0 10px 30px -10px rgba(30,40,90,0.28), 0 1px 3px rgba(30,40,90,0.08), 0 0 0 0.5px rgba(60,60,67,0.12)',
    dialog: '0 24px 60px -20px rgba(30,40,90,0.35), 0 0 0 0.5px rgba(60,60,67,0.12)',
  },
  dark: {
    popover: '0 10px 30px -10px rgba(0,0,0,0.7), 0 0 0 0.5px rgba(255,255,255,0.14)',
    dialog: '0 24px 60px -20px rgba(0,0,0,0.8), 0 0 0 0.5px rgba(255,255,255,0.14)',
  },
} as const;

export const tokens = {
  colors: colorsByMode,
  glassLevels,
  glassSurfaces,
  accents,
  backdrops,
  backdropCoverage,
  backdropSpotGeometry,
  focus,
  typography,
  spacing,
  radius,
  layout,
  motion,
  elevation,
  tabs,
} as const;
